import React, { useState } from 'react';
import { useGovernance } from '../contexts/GovernanceContext';
import { useVerification } from '../contexts/VerificationContext';
import { Vote, Clock, CheckCircle, XCircle, Plus } from 'lucide-react';
import FeatureLock from '../components/FeatureLock';
import VerificationStatus from '../components/VerificationStatus';

const Governance: React.FC = () => {
  const { proposals, userVotes, vote, createProposal } = useGovernance();
  const { canVote, canCreateProposals } = useVerification();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProposal, setNewProposal] = useState({ title: '', description: '' });
  const [toastMessage, setToastMessage] = useState('');

  const handleVote = async (proposalId: string, voteType: 'for' | 'against') => {
    if (!canVote) {
      setToastMessage('You need KYC verification to vote on proposals');
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }
    
    try {
      await vote(proposalId, voteType);
      setToastMessage(`Vote ${voteType === 'for' ? 'for' : 'against'} recorded successfully!`);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      console.error('Failed to vote:', error);
      setToastMessage('Failed to record vote. Please try again.');
      setTimeout(() => setToastMessage(''), 3000);
    }
  };

  const handleCreateProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!canCreateProposals) {
      setToastMessage('You need whitelist status to create proposals');
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }
    
    if (newProposal.title && newProposal.description) {
      try {
        await createProposal(newProposal.title, newProposal.description);
        setNewProposal({ title: '', description: '' });
        setShowCreateForm(false);
        setToastMessage('Proposal created successfully!');
        setTimeout(() => setToastMessage(''), 3000);
      } catch (error) {
        console.error('Failed to create proposal:', error);
        setToastMessage('Failed to create proposal. Please try again.');
        setTimeout(() => setToastMessage(''), 3000);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'passed': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getVotePercentage = (forVotes: number, againstVotes: number) => {
    const total = forVotes + againstVotes;
    return total > 0 ? Math.round((forVotes / total) * 100) : 0;
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slideIn">
          {toastMessage}
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Governance</h1>
            <p className="text-slate-400">Participate in syndicate decision-making</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>New Proposal</span>
          </button>
        </div>

        {/* Create Proposal Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
              <h2 className="text-xl font-bold text-white mb-4">Create New Proposal</h2>
              <form onSubmit={handleCreateProposal}>
                <div className="mb-4">
                  <label className="block text-slate-400 text-sm mb-2">Title</label>
                  <input
                    type="text"
                    value={newProposal.title}
                    onChange={(e) => setNewProposal({ ...newProposal, title: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none"
                    placeholder="Proposal title"
                    required
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-slate-400 text-sm mb-2">Description</label>
                  <textarea
                    value={newProposal.description}
                    onChange={(e) => setNewProposal({ ...newProposal, description: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:outline-none h-24 resize-none"
                    placeholder="Detailed description of the proposal"
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-2 px-4 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                  >
                    Create Proposal
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="flex-1 border border-slate-600 text-slate-300 py-2 px-4 rounded-lg hover:bg-slate-700 transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Proposals List */}
        <div className="space-y-6">
          {proposals.map((proposal) => (
            <div key={proposal.id} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">{proposal.title}</h3>
                  <p className="text-slate-400 mb-4">{proposal.description}</p>
                </div>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(proposal.status)}`}>
                  {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                </div>
              </div>

              {/* Voting Stats */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-slate-400">Votes For</span>
                  </div>
                  <div className="text-2xl font-bold text-green-400">{proposal.votesFor}</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="text-slate-400">Votes Against</span>
                  </div>
                  <div className="text-2xl font-bold text-red-400">{proposal.votesAgainst}</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Vote className="w-5 h-5 text-cyan-400" />
                    <span className="text-slate-400">Support</span>
                  </div>
                  <div className="text-2xl font-bold text-cyan-400">
                    {getVotePercentage(proposal.votesFor, proposal.votesAgainst)}%
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-slate-400 mb-2">
                  <span>Voting Progress</span>
                  <span>{getVotePercentage(proposal.votesFor, proposal.votesAgainst)}%</span>
                </div>
                <div className="progress-bar-custom">
                  <div 
                    className="progress-fill-custom"
                    style={{ width: `${getVotePercentage(proposal.votesFor, proposal.votesAgainst)}%` }}
                  ></div>
                </div>
              </div>

              {/* Voting Actions */}
              {proposal.status === 'active' && (
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleVote(proposal.id, 'for')}
                    disabled={userVotes[proposal.id] !== null || !canVote}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      userVotes[proposal.id] === 'for'
                        ? 'bg-green-500 text-white'
                        : !canVote
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                        : userVotes[proposal.id] === null
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {!canVote ? 'Verification Required' : userVotes[proposal.id] === 'for' ? 'Voted For' : 'Vote For'}
                  </button>
                  <button
                    onClick={() => handleVote(proposal.id, 'against')}
                    disabled={userVotes[proposal.id] !== null || !canVote}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      userVotes[proposal.id] === 'against'
                        ? 'bg-red-500 text-white'
                        : !canVote
                        ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                        : userVotes[proposal.id] === null
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {!canVote ? 'Verification Required' : userVotes[proposal.id] === 'against' ? 'Voted Against' : 'Vote Against'}
                  </button>
                </div>
              )}

              {/* End Date */}
              <div className="mt-4 flex items-center space-x-2 text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Ends: {proposal.endDate.toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Governance;
